import { Component, ViewChild } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import { ApiUrlHelper } from '../../../common/api-url-helper';
import { CommonService } from '../../../services/common/common.service';
import { SharedService } from '../../../services/shared-service/shared.service';
import { Location } from '@angular/common';
import { MatDialog } from '@angular/material/dialog';
import { ResetPasswordSuccessPopup } from '../../../component/reset-password-success-popup/reset-password-success-popup';
import { UserLabelConstants } from '../../../constants/LabelConstants';
import { RecaptchaWidgetComponent } from '../../../component/recaptcha-widget/recaptcha-widget';
import { CaptchaService } from '../../../services/captcha-service/captcha.service';
import { PermissionStore } from '../../../services/permission-service/permission.store';

@Component({
  selector: 'app-reset-password',
  standalone: false,
  templateUrl: './reset-password.component.html',
  styleUrl: './reset-password.component.scss'
})
export class ResetPasswordComponent {
  resetPasswordForm: FormGroup | any;
  showNewPassword: boolean = false;
  showConfirmPassword: boolean = false;
  submitted: boolean = false;
  errorConstants: any;
  labelConstants: any;
  queryString = Object();
  emailAddress: any;
  encryptedUserId: any;
  validateTime: any;
  year: any = new Date().getFullYear();
  isFirstLogin: any;
  isCaptchaVerified: boolean = false;
  @ViewChild('recaptchaWidget') recaptchaWidget?: RecaptchaWidgetComponent;

  constructor(
    private readonly formBuilder: FormBuilder,
    private readonly route: Router,
    private readonly commonService: CommonService,
    private readonly toastr: ToastrService,
    private readonly activatedRoute: ActivatedRoute,
    private readonly api: ApiUrlHelper,
    private readonly spinner: NgxSpinnerService,
    private readonly location: Location,
    private readonly sharedService: SharedService,
    private readonly dialog: MatDialog,
    public readonly captchaService: CaptchaService,
    private readonly permissionStore: PermissionStore
  ) { }

  ngOnInit(): void {
    this.sharedService.setData(UserLabelConstants.ResetPassword);
    this.isCaptchaVerified = !this.captchaService.isEnabled();
    if (this.sharedService.getValue('userId')) {
      this.location.back();
    }
    this.spinner.show();
    this.isFirstLogin = this.commonService.Decrypt(this.sharedService.getValue('isFirstLogin'));

    this.activatedRoute.paramMap.subscribe(params => {
      const id = params.get('id');
      const validity = params.get('validity');

      if (this.isFirstLogin == 'false') {
        this.encryptedUserId = id;
        this.spinner.hide();
      }
      if (id && validity) {
        let api;
        if (this.isFirstLogin == 'true') {
          api = this.api.apiUrl.Account.ValidateForgotPassword;
        }
        else {
          api = this.api.apiUrl.Account.ValidateForgotPasswordLink;
        }

        let model = {
          EncryptedUserId: id,
          EncryptedDateTime: validity
        }
        this.commonService.doPost(api, model).subscribe({
          next: (response) => {
            this.spinner.hide();
            if (!response.success) {
              this.route.navigate(['/']);
              this.toastr.error(response.message);
            }
            else {
              this.encryptedUserId = id;
            }
          }
        })
      }
      else {
        this.route.navigate(['/']);
      }
    });
    this.resetPasswordForm = this.formBuilder.group({
      NewPassword: ['', [
        Validators.required,
        Validators.pattern('(?=\\D*\\d)(?=[^a-z]*[a-z])(?=[^A-Z]*[A-Z]).{8,50}')
      ]],
      ConfirmNewPassword: ['', Validators.required]
    }, {
      validator: this.passwordMatchValidator,
    }
    );
  }

  onCaptchaResolved(): void {
    this.isCaptchaVerified = true;
  }

  onCaptchaExpired(): void {
    this.isCaptchaVerified = false;
  }

  get changePassControls() {
    return this.resetPasswordForm.controls;
  }

  togglePasswordVisibility(fieldName: string) {
    (this as any)[fieldName] = !(this as any)[fieldName];
  }

  passwordMatchValidator(formGroup: FormGroup) {
    const newPassword = formGroup.get('NewPassword');
    const confirmPassword = formGroup.get('ConfirmNewPassword');

    if (!newPassword || !confirmPassword) return null;

    const newPasswordValue = newPassword.value;
    const confirmPasswordValue = confirmPassword.value;

    if (!confirmPasswordValue) {
      // Don't set mismatch if confirm password is empty (let required handle it)
      return null;
    }

    if (newPasswordValue !== confirmPasswordValue) {
      confirmPassword.setErrors({
        ...confirmPassword.errors, // preserve existing errors like 'required'
        mismatch: true,
      });
    } else {
      // Remove only 'mismatch' error if it exists
      if (confirmPassword.hasError('mismatch')) {
        const errors = { ...confirmPassword.errors };
        delete errors['mismatch'];
        confirmPassword.setErrors(Object.keys(errors).length ? errors : null);
      }
    }
    return null;
  }

  submitResetPassForm() {
    this.submitted = true;
    if (this.resetPasswordForm.valid) {
      if (this.captchaService.isEnabled() && !this.recaptchaWidget?.hasValidToken()) {
        this.toastr.error('Please complete the security verification.');
        return;
      }

      this.isFirstLogin = this.commonService.Decrypt(this.sharedService.getValue('isFirstLogin')) === 'true';
      let api = this.api.apiUrl.Account.ResetPassword;
      let model = {
        UserId: this.isFirstLogin == true ? this.commonService.Decrypt(this.encryptedUserId) : this.encryptedUserId,
        NewPassword: this.resetPasswordForm.value.NewPassword,
        ConfirmPassword: this.resetPasswordForm.value.ConfirmNewPassword,
        IsFirstLogin: this.isFirstLogin == true,
        CaptchaToken: this.recaptchaWidget?.getToken() ?? ''
      }
      this.spinner.show();
      this.commonService.doPost(api, model).pipe().subscribe({
        next: (response) => {
          if (response.success) {
            this.spinner.hide();

            if (response.data) {
              const tokenData = response.data;

              this.sharedService.setValue('emailId', this.commonService.Encrypt(tokenData.email));
              this.sharedService.setValue('userId', this.commonService.Encrypt(tokenData.userId));
              this.sharedService.setValue('fullName', this.commonService.Encrypt(tokenData.firstName + ' ' + tokenData.lastName));
              this.sharedService.setValue('lastPasswordChange', this.commonService.Encrypt(tokenData.lastPasswordChange));
              this.permissionStore.set(tokenData.permissions);
              this.commonService.setCsrfToken(tokenData.csrfToken);
            }
            if (this.isFirstLogin == 'true') {
              this.sharedService.removeValue('token');
            }
            this.toastr.success(response.message);
            const dialogRef = this.dialog.open(ResetPasswordSuccessPopup, {
              width: '500px',
              disableClose: true,
              data: this.isFirstLogin
            });
            dialogRef.afterClosed().subscribe();
          }
          else {
            this.recaptchaWidget?.reset();
            this.toastr.error(response.message);
            this.spinner.hide();
          }
        }, error: (err: any) => {
          this.spinner.hide();
          console.error(err);
        }
      })
    }
  }

  backToLogin() {
    this.sharedService.clearStorage();
    this.route.navigate(['/login']);
  }
}